/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Executive list with schedules for a selected participating company.
 * @module components/B2BEventParticipatingCompanyForm/ParticipantUsersSection
 */

import { CircularProgress } from "@mui/material";

import ParticipantUserRow from "./ParticipantUserRow.jsx";

/**
 * Renders loading, empty, or populated participant user rows.
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ParticipantUsersSection = ({
  loadingUsers,
  companyUsers,
  selectedCustomerUsers,
  eventDateStart,
  eventDateEnd,
  eventTimeStart,
  eventTimeEnd,
  isUserSelected,
  getSchedulesForUser,
  onCheckboxChange,
  onScheduleChange,
  onRemoveSchedule,
  onAddSchedule,
  styles,
  t,
}) => {
  if (loadingUsers) {
    return (
      <div className={styles.participantUsersLoading}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if ((companyUsers?.length ?? 0) === 0) {
    return (
      <p className={styles.participantUsersEmpty}>
        {t("components.addParticipantModal.noUsers")}
      </p>
    );
  }

  return companyUsers.map((user) => {
    const userId = user._id || user.id;
    return (
      <ParticipantUserRow
        key={userId || user.nombre}
        user={user}
        selected={isUserSelected(userId)}
        schedules={getSchedulesForUser(userId)}
        userEntries={selectedCustomerUsers.filter(
          (entry) => entry._id === userId,
        )}
        eventDateStart={eventDateStart}
        eventDateEnd={eventDateEnd}
        eventTimeStart={eventTimeStart}
        eventTimeEnd={eventTimeEnd}
        onCheckboxChange={onCheckboxChange}
        onScheduleChange={onScheduleChange}
        onRemoveSchedule={onRemoveSchedule}
        onAddSchedule={onAddSchedule}
        styles={styles}
        t={t}
      />
    );
  });
};

export default ParticipantUsersSection;
